import { CommonModule, Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { UI } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import {
  CampaignService,
  type CampaignChannel,
  type CampaignPreview,
  type CampaignProcessor,
  type CampaignTemplateOption,
} from '../../services/campaign.service';
import { MailingService, choiceValue, type CampaignCategory } from '../../services/mailing.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';

/** Message of an API error body (`{error}` or DRF field errors), never a raw exception. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const body = err instanceof HttpErrorResponse ? err.error : null;
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if (typeof record['error'] === 'string') {
      return record['error'];
    }
    const first = Object.entries(record)[0];
    if (first) {
      const value = Array.isArray(first[1]) ? first[1][0] : first[1];
      return `${first[0]}: ${String(value)}`;
    }
  }
  return fallback;
}

/**
 * New campaign: pick the channel and the file processor, choose a template (or let the file carry its own messages),
 * check the file (nothing is stored; the report lists rows parsed, skipped without a recipient, invalid and
 * duplicates), then create the campaign. Sending only starts from the campaign page.
 */
@Component({
  selector: 'app-campaign-create',
  standalone: true,
  imports: [CommonModule, FormsModule, ...UI],
  template: `
    <div class="mx-auto w-full max-w-4xl px-4 py-3">
      <div class="mb-3 flex items-center gap-2">
        <button type="button" uiButton variant="outline" size="sm" (click)="goBack()">Back</button>
        <h1 class="m-0 font-heading text-lg font-semibold text-fg">New message campaign</h1>
      </div>
      <p class="mb-4 text-sm text-muted">
        Load a spreadsheet (.xlsx) or CSV. The processor decides which rows become messages; rows it skips on purpose
        (for example a timesheet row without a phone number) are counted, not reported as errors. Nothing is sent until
        you start the campaign.
      </p>

      @if (error()) {
        <ui-alert variant="danger" class="mb-3 block">{{ error() }}</ui-alert>
      }

      <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
        @if (categories().length) {
          <ui-form-field
            class="md:col-span-2"
            label="Category (optional)"
            hint="Prefills the processor, template, sender and options. Every field stays editable."
          >
            <select uiInput [ngModel]="categoryId()" (ngModelChange)="applyCategory($event)" name="category">
              <option value="">No category</option>
              @for (c of categories(); track c.id) {
                <option [value]="c.id">{{ c.name }}</option>
              }
            </select>
          </ui-form-field>
        }
        <ui-form-field label="Name">
          <input uiInput type="text" [(ngModel)]="name" name="name" placeholder="Timesheet June 2026" />
        </ui-form-field>
        <ui-form-field label="Channel">
          <select uiInput [ngModel]="channel()" (ngModelChange)="setChannel($event)" name="channel">
            <option value="SMS">SMS</option>
            <option value="EMAIL">Email</option>
          </select>
          @if (fromCategory('channel')) {
            <p class="m-0 mt-1 text-xs text-muted">from category</p>
          }
        </ui-form-field>
        <ui-form-field label="File processor">
          <select uiInput [(ngModel)]="processor" name="processor" (ngModelChange)="onProcessor()">
            @for (p of channelProcessors(); track p.code) {
              <option [value]="p.code">{{ p.label }}</option>
            }
          </select>
          @if (fromCategory('processor')) {
            <p class="m-0 mt-1 text-xs text-muted">from category</p>
          }
        </ui-form-field>
        <ui-form-field label="Template">
          <select uiInput [(ngModel)]="template" name="template">
            <option value="">
              {{ selectedProcessor()?.default_template_code ? 'Processor default' : 'None (messages come from the file)' }}
            </option>
            @for (t of templates(); track t.id) {
              <option [value]="t.id">{{ t.name }}</option>
            }
          </select>
          @if (fromCategory('template')) {
            <p class="m-0 mt-1 text-xs text-muted">from category</p>
          }
        </ui-form-field>
        @if (channel() === 'EMAIL') {
          <ui-form-field label="Subject (used when the file has none)">
            <input uiInput type="text" [(ngModel)]="subject" name="subject" />
            @if (fromCategory('subject')) {
              <p class="m-0 mt-1 text-xs text-muted">from category</p>
            }
          </ui-form-field>
        }
        <ui-form-field [label]="channel() === 'SMS' ? 'SMS sender name (optional)' : 'From name (optional)'">
          <input uiInput type="text" [(ngModel)]="senderName" name="senderName" maxlength="64" />
          @if (fromCategory('senderName')) {
            <p class="m-0 mt-1 text-xs text-muted">from category</p>
          }
          @if (channel() === 'SMS') {
            <p class="m-0 mt-1 text-xs text-muted">
              Letters and digits, up to 11 characters (extra characters are removed). Sent as the sender by providers that
              support it (Orange SMPP); others use their default sender. A file column <code>sender_name</code> overrides it per row.
            </p>
          }
        </ui-form-field>
        @if (processor === 'timesheet') {
          <ui-form-field label="Expected hours per day (overtime note)">
            <input uiInput type="number" min="1" max="24" [(ngModel)]="hoursPerDay" name="hoursPerDay" />
          </ui-form-field>
          <ui-form-field label="Period start (optional)">
            <input uiInput type="date" [(ngModel)]="dateFrom" name="dateFrom" />
          </ui-form-field>
          <ui-form-field label="Period end (optional)">
            <input uiInput type="date" [(ngModel)]="dateTo" name="dateTo" />
          </ui-form-field>
          <p class="m-0 text-xs text-muted md:col-span-2">
            Leave the period empty to detect it from the file's date columns.
          </p>
        }
        <ui-form-field label="Skip repeated recipients">
          <label class="flex items-center gap-2 text-sm text-fg">
            <input type="checkbox" [(ngModel)]="dedupe" name="dedupe" /> Send only the first row of each recipient
          </label>
        </ui-form-field>
        <ui-form-field class="md:col-span-2" label="File (.xlsx or .csv)">
          <input uiInput type="file" accept=".xlsx,.csv" (change)="onFile($event)" />
        </ui-form-field>
      </div>

      @if (selectedProcessor(); as p) {
        <p class="mb-3 text-xs text-muted">
          Required columns: <code>{{ p.required_columns.join(', ') }}</code>
          @if (p.optional_columns.length) {
            · optional: <code>{{ p.optional_columns.join(', ') }}</code>
          }
        </p>
      }

      <div class="mb-4 flex flex-wrap gap-2">
        <button type="button" uiButton variant="outline" [disabled]="!ready() || busy()" (click)="check()">
          Check file
        </button>
        <button type="button" uiButton [disabled]="!ready() || busy()" (click)="create()">Create campaign</button>
      </div>

      @if (preview(); as pv) {
        <ui-card title="File report">
          <div class="grid grid-cols-2 gap-3 md:grid-cols-6">
            @for (t of tiles(); track t.label) {
              <div class="rounded-md border border-border p-2 text-center">
                <div class="text-xl font-semibold" [class]="t.cls">{{ t.value }}</div>
                <div class="text-xs text-muted">{{ t.label }}</div>
              </div>
            }
          </div>
          @if (pv.report.period?.start) {
            <p class="mt-2 text-sm text-muted">Period: {{ pv.report.period?.start }} → {{ pv.report.period?.end }}</p>
          }
        </ui-card>

        @if (pv.samples.length) {
          <ui-card title="First messages" class="mt-3 block">
            @for (s of pv.samples; track s.line) {
              <div class="mb-2 rounded-md bg-surface-muted p-2 text-sm">
                <div class="mb-1 text-xs text-muted">Line {{ s.line }} → {{ s.recipient }}</div>
                @if (s.subject) {
                  <div class="font-semibold">{{ s.subject }}</div>
                }
                <pre class="m-0 whitespace-pre-wrap font-sans">{{ s.message }}</pre>
              </div>
            }
          </ui-card>
        }

        @if (pv.report.issues.length) {
          <ui-card title="Rows rejected" class="mt-3 block">
            <div uiTableWrap>
              <table uiTable>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Name</th>
                    <th>Recipient</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  @for (i of pv.report.issues; track i.line) {
                    <tr>
                      <td>{{ i.line }}</td>
                      <td>{{ i.name }}</td>
                      <td>{{ i.recipient }}</td>
                      <td class="text-xs text-muted">{{ i.code }} — {{ i.detail }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            @if (pv.report.issues_truncated) {
              <p class="mt-1 text-xs text-muted">… and {{ pv.report.issues_truncated }} more.</p>
            }
          </ui-card>
        }
      }
    </div>
  `,
})
export class CampaignCreateComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;

  private readonly svc = inject(CampaignService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly shellRoutes = inject(ShellRoutesService);

  name = '';
  processor = '';
  template = '';
  subject = '';
  senderName = '';
  hoursPerDay = 8;
  dateFrom = '';
  dateTo = '';
  dedupe = false;

  private readonly mailings = inject(MailingService);
  senderEmail = '';
  /** Values copied from the selected category, to show a "from category" hint while they are unchanged. */
  private prefill: Record<string, string> = {};

  readonly categories = signal<CampaignCategory[]>([]);
  readonly categoryId = signal('');
  readonly channel = signal<CampaignChannel>('SMS');
  readonly processors = signal<CampaignProcessor[]>([]);
  readonly templates = signal<CampaignTemplateOption[]>([]);
  readonly file = signal<File | null>(null);
  readonly preview = signal<CampaignPreview | null>(null);
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);

  readonly channelProcessors = computed(() => this.processors().filter((p) => p.channels.includes(this.channel())));
  readonly selectedProcessor = computed(() => this.processors().find((p) => p.code === this.processor) ?? null);
  readonly ready = computed(() => !!this.file() && !!this.processor);
  readonly tiles = computed(() => {
    const parsed = this.preview()?.report.parsed;
    if (!parsed) {
      return [];
    }
    return [
      { label: 'Rows parsed', value: parsed.rows_parsed, cls: '' },
      { label: 'With recipient', value: parsed.with_recipient, cls: '' },
      { label: 'Skipped (none)', value: parsed.skipped_no_recipient, cls: 'text-muted' },
      { label: 'Valid', value: parsed.valid, cls: 'text-success' },
      { label: 'Invalid', value: parsed.invalid, cls: parsed.invalid ? 'text-danger' : '' },
      { label: 'Repeated', value: parsed.duplicates, cls: parsed.duplicates ? 'text-warning' : '' },
    ];
  });

  ngOnInit(): void {
    this.svc.processors().subscribe({
      next: (list) => {
        this.processors.set(list);
        this.processor = this.channelProcessors()[0]?.code ?? '';
      },
      error: () => this.error.set('Could not load the file processors.'),
    });
    this.loadTemplates();
    this.mailings.categories('FILE_IMPORT').subscribe({
      next: (page) => this.categories.set(page.results.filter((c) => choiceValue(c.kind) === 'FILE_IMPORT')),
      error: () => this.categories.set([]),
    });
  }

  /** True while a field still holds the value its category provided. */
  fromCategory(field: string): boolean {
    const current: Record<string, string> = {
      channel: this.channel(),
      processor: this.processor,
      template: this.template,
      subject: this.subject,
      senderName: this.senderName,
    };
    return !!this.categoryId() && this.prefill[field] !== undefined && this.prefill[field] === current[field];
  }

  /** Prefill the form from a category (client-side only: the upload endpoint does not take a category). */
  applyCategory(id: string): void {
    this.categoryId.set(id);
    this.prefill = {};
    const cat = this.categories().find((c) => c.id === id);
    if (!cat) return;
    const channel = choiceValue(cat.channel) as CampaignChannel;
    if (channel === 'SMS' || channel === 'EMAIL') {
      this.setChannel(channel);
    }
    this.processor = cat.processor || this.processor;
    this.template = cat.template ?? '';
    this.subject = cat.subject ?? '';
    this.senderName = cat.sender_name ?? '';
    this.senderEmail = cat.sender_email ?? '';
    const opts = cat.processor_options ?? {};
    if (typeof opts['dedupe'] === 'boolean') this.dedupe = opts['dedupe'];
    if (typeof opts['hours_per_day'] === 'number') this.hoursPerDay = opts['hours_per_day'];
    this.prefill = {
      channel: this.channel(),
      processor: this.processor,
      template: this.template,
      subject: this.subject,
      senderName: this.senderName,
    };
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  setChannel(value: CampaignChannel): void {
    this.channel.set(value);
    this.template = '';
    this.preview.set(null);
    if (!this.channelProcessors().some((p) => p.code === this.processor)) {
      this.processor = this.channelProcessors()[0]?.code ?? '';
    }
    this.loadTemplates();
  }

  onProcessor(): void {
    this.preview.set(null);
  }

  onFile(ev: Event): void {
    const picked = (ev.target as HTMLInputElement).files?.[0] ?? null;
    this.file.set(picked);
    this.preview.set(null);
    this.error.set(null);
    if (picked && !this.name) {
      this.name = picked.name.replace(/\.[^.]+$/, '');
    }
  }

  check(): void {
    this.run(() =>
      this.svc.preview(this.input()).subscribe({
        next: (pv) => {
          this.preview.set(pv);
          this.busy.set(false);
        },
        error: (err) => this.fail(err, 'The file could not be checked.'),
      }),
    );
  }

  create(): void {
    this.run(() =>
      this.svc.upload(this.input()).subscribe({
        next: (campaign) => {
          this.busy.set(false);
          void this.router.navigate(this.shellRoutes.commands('manage', this.entityConfig.key, campaign.id));
        },
        error: (err) => this.fail(err, 'The campaign could not be created.'),
      }),
    );
  }

  private input() {
    const options: Record<string, unknown> = { dedupe: this.dedupe };
    if (this.processor === 'timesheet') {
      options['hours_per_day'] = this.hoursPerDay;
      if (this.dateFrom) options['date_from'] = this.dateFrom;
      if (this.dateTo) options['date_to'] = this.dateTo;
    }
    return {
      file: this.file() as File,
      name: this.name,
      channel: this.channel(),
      processor: this.processor,
      template: this.template || undefined,
      subject: this.subject,
      sender_name: this.senderName,
      sender_email: this.senderEmail,
      options,
    };
  }

  private run(call: () => void): void {
    this.busy.set(true);
    this.error.set(null);
    call();
  }

  private fail(err: unknown, fallback: string): void {
    this.busy.set(false);
    this.error.set(apiErrorMessage(err, fallback));
  }

  private loadTemplates(): void {
    this.svc.templates(this.channel()).subscribe({
      next: (page) => this.templates.set(page.results),
      error: () => this.templates.set([]),
    });
  }
}
